/*eslint-disable*/
import React, { useEffect, useRef } from "react";
import { Container } from "reactstrap";
import edgeLogo from "assets/img/edge/Edge AI Logo.png";

function PresentationHeader() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let nodes = [];
    let pulses = [];
    let w, h, frame = 0;

    const ORANGE = [245, 162,   1];
    const GOLD   = [255, 186,  66];
    const BLUE   = [  0,  83, 122];

    const resize = () => {
      w = canvas.width  = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      buildNodes();
    };

    class Node {
      constructor() {
        this.x  = Math.random() * w;
        this.y  = Math.random() * h;
        this.vx = (Math.random() - 0.5) * 0.28;
        this.vy = (Math.random() - 0.5) * 0.28;
        const r = Math.random();
        this.col = r > 0.65 ? ORANGE : r > 0.35 ? GOLD : BLUE;
        this.r   = Math.random() * 1.6 + 1.2;
        this.phase = Math.random() * Math.PI * 2;
        this.phSp  = Math.random() * 0.02 + 0.008;
        this.hub   = Math.random() > 0.8;
      }
      tick() {
        this.x += this.vx;
        this.y += this.vy;
        this.phase += this.phSp;
        if (this.x < 0 || this.x > w) this.vx *= -1;
        if (this.y < 0 || this.y > h) this.vy *= -1;
      }
      draw() {
        const [r, g, b] = this.col;
        const rad = this.r + Math.sin(this.phase) * 0.5;
        if (this.hub) {
          const grd = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, rad * 5);
          grd.addColorStop(0, `rgba(${r},${g},${b},0.22)`);
          grd.addColorStop(1, `rgba(${r},${g},${b},0)`);
          ctx.fillStyle = grd;
          ctx.beginPath();
          ctx.arc(this.x, this.y, rad * 5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = `rgba(${r},${g},${b},${this.hub ? 0.9 : 0.5})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, rad, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    class Pulse {
      constructor(a, b) {
        this.a  = a;
        this.b  = b;
        this.t  = 0;
        this.sp = Math.random() * 0.012 + 0.006;
        this.c  = a.col;
      }
      tick() { this.t += this.sp; return this.t < 1.05; }
      draw() {
        if (this.t >= 1) return;
        const [r, g, b] = this.c;
        const x = this.a.x + (this.b.x - this.a.x) * this.t;
        const y = this.a.y + (this.b.y - this.a.y) * this.t;
        const grd = ctx.createRadialGradient(x, y, 0, x, y, 8);
        grd.addColorStop(0, `rgba(${r},${g},${b},0.85)`);
        grd.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(${r},${g},${b},1)`;
        ctx.beginPath();
        ctx.arc(x, y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const buildNodes = () => {
      const n = Math.min(68, Math.floor(w / 15));
      nodes = Array.from({ length: n }, () => new Node());
    };

    const drawEdges = () => {
      const MAX = 140;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d2 = dx * dx + dy * dy;
          if (d2 > MAX * MAX) continue;
          const dist = Math.sqrt(d2);
          const alpha = 1 - dist / MAX;
          const hot = nodes[i].hub || nodes[j].hub;
          if (hot) {
            const grd = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y);
            grd.addColorStop(0, `rgba(245,162,1,${alpha * 0.5})`);
            grd.addColorStop(1, `rgba(255,186,66,${alpha * 0.35})`);
            ctx.strokeStyle = grd;
            ctx.lineWidth = 1.0;
          } else {
            ctx.strokeStyle = `rgba(0,60,90,${alpha * 0.22})`;
            ctx.lineWidth = 0.6;
          }
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(nodes[j].x, nodes[j].y);
          ctx.stroke();

          if (frame % 90 === 0 && hot && dist < 100 && Math.random() > 0.6) {
            pulses.push(new Pulse(nodes[i], nodes[j]));
          }
        }
      }
    };

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      ctx.clearRect(0, 0, w, h);
      frame++;
      drawEdges();
      pulses = pulses.filter(p => { p.draw(); return p.tick(); });
      nodes.forEach(n => { n.tick(); n.draw(); });
    };

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    loop();

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  const stats = [
    { value: "10+", label: "Years" },
    { value: "50+", label: "Projects" },
    { value: "LA",  label: "California" },
  ];

  const tags = ["Snowflake", "dbt", "Airflow", "AI Agents", "n8n", "Python", "AWS", "Azure", "Machine Learning"];

  return (
    <div className="edge-hero">
      <canvas ref={canvasRef} className="edge-hero-canvas" />
      <div className="edge-blueprint-grid" />
      <div className="edge-hero-overlay-bottom" />

      <Container>
        <div className="edge-hero-content">

          <div className="edge-status-badge">
            <span className="edge-status-dot" />
            Available · Los Angeles, CA ·{" "}
            <a href="tel:+13232066068" className="edge-status-phone">
              (323) 206-6068
            </a>
          </div>

          <h1>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "2px" }}>
              <img src={edgeLogo} alt="Edge Ventures" style={{ width: "72px", flexShrink: 0, marginTop: "6px" }} />
              <span>Edge</span>
            </div>
            <span className="edge-accent">Ventures</span>
          </h1>

          <div className="edge-hero-rule" />

          <p className="edge-hero-desc">
            We build the <strong>AI, data and automation systems</strong> that move
            the numbers you're accountable for.
          </p>

          <div className="edge-cta-row">
            <a href="/products" className="edge-btn-primary">
              Product Support
            </a>
            <a
              href="#past-work"
              className="edge-btn-ghost"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('past-work').scrollIntoView({ behavior: 'smooth' });
              }}
            >
              View Our Work ↓
            </a>
          </div>

          <div className="edge-stats-bar">
            {stats.map(s => (
              <div key={s.value} className="edge-stat-item">
                <span className="edge-stat-value">{s.value}</span>
                <span className="edge-stat-label">{s.label}</span>
              </div>
            ))}
            <div className="edge-tech-row">
              {tags.map(t => (
                <span key={t} className="edge-tech-tag">{t}</span>
              ))}
            </div>
          </div>

        </div>
      </Container>
    </div>
  );
}

export default PresentationHeader;
